import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBasePurchase } from '../../../hooks/purchases/useBasePurchase';
import { useDocumentType } from '../../../hooks/useDocumentType';
import { useSupplier } from '../../../hooks/useSupplier';
import { useAllowedOptions } from '../../../hooks/settings/useAllowedOptions';
import { usePdfType } from '../../../hooks/pdf/usePdfType';
import { usePurchaseButtonActions } from '../../../hooks/purchases/usePurchaseButtonActions';
import { usePurchasePdfModalActions } from '../../../features/purchases/history/purchasePdfModalActions';
import { usePurchasePreviewHandler } from '../../../hooks/purchases/pdf/usePurchasePreviewHandler';
import { buildPurchaseFilterPayload } from '../../../types/purchases/filters/PurchaseFilterBuilder';
import { PurchaseSummary } from '../../../types/purchases/BasePurchase';
import { getPurchaseTableActions } from '../../../features/purchases/history/purchaseHistoryTableActions';
import { getPurchaseHistoryFilters } from '../../../features/purchases/history/purchaseHistoryFilters';
import DefaultLayout from '../../../layout/DefaultLayout';
import AdvancedRecordTable from '../../../components/Tables/advancedRecord/AdvancedRecordTable';
import { purchaseHistoryColumns } from '../../../features/purchases/history/purchaseHistoryColumns';
import PendingPurchaseModal from '../../../components/Modals/PendingPurchaseModal';
import FinalizedPurchaseModal from '../../../components/purchases/modals/FinalizedPurchaseModal';
import PurchaseViewModal from '../../../components/Modals/Purchases/PurchaseViewModal';
import PdfModal from '../../../components/Modals/pdf/PdfModal';
import { usePurchaseInvalidation } from '../../../hooks/purchases/invalidation/usePurchaseInvalidation';
import { usePurchaseDtePreview } from '../../../hooks/purchases/usePurchaseDtePreview';
import DtePdfModal from '../../../components/Modals/DtePdfModal/DtePdfModal';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import DteErrorModal from '../../../components/Modals/DteErrorModal';
import { DteErrorDetail } from '../../../types/dte/DteErrorDetail';
import useAlerts from '../../../hooks/useAlerts';
import PurchaseButtonActionService from '../../../hooks/purchases/pdf/PurchaseButtonActionService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';
import useConfirm from '../../../hooks/useConfirmModal';

const PurchaseHistory: React.FC = () => {
  const {
    handlerGetPendingTotal,
    handlerGetPendingPurchases,
    pendingPurchaseCount,
    pendingPurchaseList,
    handlerGetFinalizedTotal,
    handlerGetFinalizedPurchases,
    finalizedPurchaseCount,
    finalizedPurchaseList,
    handlerGetFilteredPurchases,
    handlerCancelPendingPurchase,
    filteredPurchaseList,
    paginator,
    pageSize,
    updatePageSize,
    handlerViewPurchaseById,
    handlerEditPurchaseById,
    handlerSettlePurchaseProgressById,
    purchaseToViewLoaded,
    handleCloseViewModal,
    handlerClearFilteredPurchaseList,
    showPdfModal,
    openPdfModal,
    closePdfModal,
    setMetaData,
    clearMetaData,
    showDtePdfModal,
    closeDtePdfModal,
  } = useBasePurchase();
  const { page } = useParams();
  const currentPage = page ? parseInt(page, 10) : 0;
  const navigate = useNavigate();

  const { fetchDocumentTypeDropdown, documentTypeDropdown } = useDocumentType();
  const { handlerGetSuppliersForDropdown, suppliersDropdown } = useSupplier();
  const { getCodes, fetchCodesByModuleAndTarget } = useAllowedOptions();
  const { fetchPdfTypeDropdown, pdfTypeDropdown } = usePdfType();
  const { handlerConsultDTE, handlerDownloadJson, handlerForceAnnulRejectedPurchase, handlerSendByEmail, handlerGetDteError, handlerResendDte } = usePurchaseButtonActions();
  const { show: showAlert } = useAlerts();
  const confirm = useConfirm();
  const { hasRole } = useAuthUser();
  const canCreateReturn = hasRole('ADMIN') || hasRole('SUPPORT');
  // Reenviar DTE de una compra rechazada: solo ADMIN y SUPPORT administran Compras hoy.
  const canResendDte = hasRole('ADMIN') || hasRole('SUPPORT');
  const [dteErrorOpen, setDteErrorOpen] = useState(false);
  const [dteErrorLoading, setDteErrorLoading] = useState(false);
  const [dteErrorUnavailable, setDteErrorUnavailable] = useState(false);
  const [dteError, setDteError] = useState<DteErrorDetail | null>(null);

  const openDteError = async (purchaseId: number) => {
    setDteErrorOpen(true); setDteErrorLoading(true); setDteErrorUnavailable(false); setDteError(null);
    try { const response = await handlerGetDteError(purchaseId); setDteError(response.data); }
    catch (error: any) {
      if (error?.response?.status === 404) setDteErrorUnavailable(true);
      else showAlert('error', 'Error', 'No se pudo consultar la respuesta de Hacienda.');
    } finally { setDteErrorLoading(false); }
  };
  const pdfModalActions = usePurchasePdfModalActions();

  const { loadPrefillFromPurchase } = usePurchaseInvalidation();

  const {
    base64Pdf,
    setBase64Pdf,
    handleViewDocument: handleViewDocument,
    fileName,
  } = usePurchasePreviewHandler();

  const {
    base64Dte,
    handleViewDte,
  } = usePurchaseDtePreview();

  const [modalOpen, setModalOpen] = useState(false);
  const [finalizedModalOpen, setFinalizedModalOpen] = useState(false);
  const [showCancellationControls, setShowCancellationControls] =
    useState(false);

  const [filters, setFilters] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);

  // 🔁 Filtros => se aplica en la página 0
  const handleFilterChange = (newFilters: any) => {
    const payload = buildPurchaseFilterPayload(newFilters);
    setFilters(payload);
    setFiltersApplied(true); // ✅ marca que ya se aplicaron filtros
    handlerGetFilteredPurchases(payload, 0, pageSize);
  };

  // 🔁 Cambio de página => conserva filtros actuales
  const handlePageChange = (page: number) => {
    handlerGetFilteredPurchases(filters, page, pageSize);
  };

  // 🔁 Cambio de tamaño de página => aplica filtros actuales desde página 0
  const handlePageSizeChange = (size: number) => {
    updatePageSize(size);
    handlerGetFilteredPurchases(filters, 0, size);
  };

  useEffect(() => {
    if (filtersApplied) {
      handlerGetFilteredPurchases(filters, currentPage, pageSize);
    }
  }, [currentPage]);

  // ⚡ Carga inicial de catálogos
  useEffect(() => {
    handlerGetPendingTotal(); // Total pendientes
    handlerGetFinalizedTotal(); // ✅ Total finalizadas
    fetchDocumentTypeDropdown(); // Catálogo tipos de documento
    handlerGetSuppliersForDropdown(); // Catálogo proveedores
    fetchPdfTypeDropdown();
  }, []);

  // ⚡ Cargar compras pendientes al abrir el modal
  useEffect(() => {
    if (modalOpen) {
      handlerGetPendingPurchases();
    }
  }, [modalOpen]);

  // ⚡ Cargar compras pendientes al abrir el modal
  useEffect(() => {
    if (finalizedModalOpen) {
      handlerGetFinalizedPurchases(); // ⚡ También carga las compras finalizadas
    }
  }, [finalizedModalOpen]);

  useEffect(() => {
    fetchCodesByModuleAndTarget('PURCHASES', 'DOCUMENT_TYPE');
  }, []);

  const handleContinue = async (purchase: PurchaseSummary) => {
    await handlerEditPurchaseById(purchase.id); // esto setea el form
  };

  const handleSettle = async (purchase: PurchaseSummary) => {
    await handlerSettlePurchaseProgressById(purchase.id);
  };

  const handleCancelPending = (id: number) => {
    confirm({
      title: 'Cancelar compra pendiente',
      message:
        'La compra quedará cancelada y no podrá continuarse. Esto no es una anulación fiscal. ¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, cancelar',
      cancelText: 'Volver',
      onConfirm: async () => {
        const ok = await handlerCancelPendingPurchase(id);
        if (ok) {
          handlerGetFilteredPurchases(filters, currentPage, pageSize);
          handlerGetPendingTotal();
        }
      },
    });
  };

  const loadPrefillFromHistory = async (purchaseId: number) => {
    const purchase = filteredPurchaseList.find((item) => item.id === purchaseId);
    if (purchase) await loadPrefillFromPurchase(purchase);
  };

  const downloadInvoicePdf = (id: number) => {
    setMetaData(id);
    openPdfModal();
  };

  const getActions = (row: any) =>
    getPurchaseTableActions({
      row,
      canCreateReturn,
      canResendDte,
      handlers: {
        onView: handlerViewPurchaseById,
        onCancel: loadPrefillFromHistory,
        onCancelPending: handleCancelPending,
        onContinue: handlerEditPurchaseById,
        onDownloadPdf: downloadInvoicePdf,
        onConsultDTE: handlerConsultDTE,
        onExportJson: handlerDownloadJson,
        onViewInvalidDte: handleViewDte,
        onForceAnnulRejectedPurchase: handlerForceAnnulRejectedPurchase,
        onSendEmail: handlerSendByEmail,
        onViewDteError: openDteError,
        onCreateReturn: (id: number) => navigate(`/purchases/returns/${id}`),
        onResendDTE: handlerResendDte,
      },
    });

  const allowedDocumentCodes = getCodes('PURCHASES', 'DOCUMENT_TYPE');

  const allowedDocumentTypes = (documentTypeDropdown ?? []).filter((doc) =>
    allowedDocumentCodes.includes(doc.code)
  );

  const filterFields = getPurchaseHistoryFilters(
    allowedDocumentTypes,
    suppliersDropdown
  );

  return (
    <DefaultLayout>
      <BreadcrumbTwo />
      
      <AdvancedRecordTable
        columns={purchaseHistoryColumns}
        data={filteredPurchaseList}
        documentTypes={documentTypeDropdown}
        suppliers={suppliersDropdown}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        getActions={getActions}
        paginator={paginator}
        onResetFilters={handlerClearFilteredPurchaseList}
        url="/purchase-history"
        pageSize={pageSize}
        noDataMessage="No hay resultados. Pruebe ajustando los filtros de búsqueda."
        topLeftActions={[
          {
            label: 'Compras pendientes',
            onClick: () => setModalOpen(true),
            count: pendingPurchaseCount,
          },
          {
            label: 'Compras por asentar',
            onClick: () => setFinalizedModalOpen(true),
            count: finalizedPurchaseCount,
          },
        ]}
        topRightAction={{
          label: 'Nueva Compra',
          href: '/purchase-register',
        }}
      />

      <PdfModal
        isOpen={showPdfModal}
        onClose={closePdfModal}
        documentOptions={pdfTypeDropdown}
        actions={pdfModalActions}
        onViewDocument={handleViewDocument}
        onReset={() => {
          clearMetaData();
          setBase64Pdf(undefined);
        }}
        base64Pdf={base64Pdf}
        fileName={fileName}
      />

      <DtePdfModal
        isOpen={showDtePdfModal}
        onClose={closeDtePdfModal}
        base64Pdf={base64Dte}
        title="Vista previa DTE"
        maxWidth="max-w-6xl"
        maxHeight="max-h-[90vh]"
      />

      <PendingPurchaseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        purchases={pendingPurchaseList}
        onContinue={handleContinue}
        onCancel={(purchase) =>
          confirm({
            title: 'Cancelar compra pendiente',
            message:
              'La compra quedará cancelada y no podrá continuarse. Esto no es una anulación fiscal. ¿Deseas continuar?',
            icon: 'warning',
            confirmText: 'Sí, cancelar',
            cancelText: 'Volver',
            onConfirm: async () => {
              const ok = await handlerCancelPendingPurchase(purchase.id);
              if (ok) {
                handlerGetPendingPurchases();
                handlerGetPendingTotal();
                if (filtersApplied) {
                  handlerGetFilteredPurchases(filters, currentPage, pageSize);
                }
              }
            },
          })
        }
      />

      <FinalizedPurchaseModal
        isOpen={finalizedModalOpen}
        onClose={() => setFinalizedModalOpen(false)}
        purchases={finalizedPurchaseList}
        onSettle={handleSettle}
      />

      <PurchaseViewModal
        isOpen={purchaseToViewLoaded}
        onClose={() => {
          setShowCancellationControls(false);
          handleCloseViewModal();
        }}
        showCancellationControls={showCancellationControls}
      />
      <DteErrorModal
        isOpen={dteErrorOpen}
        detail={dteError}
        loading={dteErrorLoading}
        unavailable={dteErrorUnavailable}
        onDownload={dteError ? () => void PurchaseButtonActionService.downloadDteResponse(Number(dteError.documentId)) : undefined}
        onClose={() => setDteErrorOpen(false)}
      />
    </DefaultLayout>
  );
};

export default PurchaseHistory;
